import React, { useState, useEffect, useCallback } from "react";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import { Table, Button, Modal, Badge, Spinner, Tabs, Tab, Card, Row, Col, ListGroup, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getApiEndpoint } from "../../../../utils/apiConfig";
import "./OrderManager.scss";

const OrderManager = () => {
  const [orders, setOrders] = useState([]);
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCalculations, setLoadingCalculations] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [calculationsPage, setCalculationsPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [calculationsTotalPages, setCalculationsTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("calculations");
  const pageSize = 10;

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      
      // Build query parameters
      let queryParams = `?page=${currentPage}&limit=${pageSize}`;
      
      // Add status filter if not "all"
      if (statusFilter !== "all") {
        queryParams += `&status=${statusFilter === "active" ? "true" : "false"}`;
      }
      
      // Add date filters if provided
      if (dateFilter.startDate) {
        queryParams += `&startDate=${dateFilter.startDate}`;
      }
      if (dateFilter.endDate) {
        queryParams += `&endDate=${dateFilter.endDate}`;
      }
      
      // Add search term if provided
      if (searchTerm) {
        queryParams += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // Add includeDetails parameter to get more data
      queryParams += '&includeDetails=true';
      
      // Use the correct endpoint with getApiEndpoint utility
      // 'repairs/orders/admin' is the correct endpoint to fetch all orders saved by users
      const endpoint = getApiEndpoint(`repairs/orders/admin${queryParams}`);
      console.log("Fetching orders from:", endpoint);
      
      const response = await api.get(endpoint);
      console.log("API Response:", response.data);
      
      if (response.data && response.data.data) {
        setOrders(response.data.data);
        setTotalPages(Math.ceil(response.data.count / pageSize) || 1);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch orders. Please try again later.",
        icon: "error",
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, dateFilter, searchTerm]);

  // Fetch repair calculations from API
  const fetchCalculations = useCallback(async () => {
    setLoadingCalculations(true);
    try {
      const api = axiosWithAuth();
      
      // Build query parameters
      let queryParams = `?page=${calculationsPage}&limit=${pageSize}`;
      
      // Add status filter if not "all"
      if (statusFilter !== "all") {
        queryParams += `&status=${statusFilter}`;
      }
      
      // Add date filters if provided
      if (dateFilter.startDate) {
        queryParams += `&startDate=${dateFilter.startDate}`;
      }
      if (dateFilter.endDate) {
        queryParams += `&endDate=${dateFilter.endDate}`;
      }
      
      // Add search term if provided
      if (searchTerm) {
        queryParams += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // Use the new endpoint for repair calculations
      const endpoint = getApiEndpoint(`repairs/calculations/admin${queryParams}`);
      console.log("Fetching repair calculations from:", endpoint);
      
      const response = await api.get(endpoint);
      console.log("Calculations API Response:", response.data);
      
      if (response.data && response.data.data) {
        setCalculations(response.data.data);
        setCalculationsTotalPages(Math.ceil(response.data.count / pageSize) || 1);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Failed to fetch repair calculations:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch repair calculations. Please try again later.",
        icon: "error",
        timer: 3000
      });
    } finally {
      setLoadingCalculations(false);
    }
  }, [calculationsPage, statusFilter, dateFilter, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);
  
  useEffect(() => {
    fetchCalculations();
  }, [fetchCalculations]);

  // View order details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
    
    // Now we need to use the breakdownId for details if available
    const detailId = order.breakdownId || order.id || order._id;
    fetchOrderDetails(detailId);
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId) => {
    setLoadingDetails(true);
    try {
      const api = axiosWithAuth();
      
      // Use the correct endpoint with getApiEndpoint utility
      // 'repairs/breakdown-materials/:id' is the correct endpoint for getting material details
      const endpoint = getApiEndpoint(`repairs/breakdown-materials/${orderId}`);
      console.log("Fetching order details from:", endpoint);
      
      const response = await api.get(endpoint);
      console.log("Order details response:", response.data);
      
      if (!response.data) {
        throw new Error('Invalid response format');
      }

      // Format the response data to match our component structure
      // The breakdown-materials endpoint returns a structure different from the admin endpoint
      const orderData = {
        _id: orderId,
        ...response.data,
        breakdown: response.data.breakdown || {},
        costs: response.data.costs || {},
        currency: response.data.costs?.currency || "AZN"
      };
      
      // Make sure we have valid rooms data
      if (!orderData.breakdown.rooms || !Array.isArray(orderData.breakdown.rooms)) {
        orderData.breakdown.rooms = [];
      }
      
      // Process the rooms to ensure they have proper area values
      orderData.breakdown.rooms = orderData.breakdown.rooms.map(room => {
        // Ensure room has a valid area
        if (!room.area || parseFloat(room.area) <= 0) {
          // Calculate from materials or use default
          let calculatedArea = 0;
          
          if (Array.isArray(room.materials) && room.materials.length > 0) {
            // Sum up areas from all materials
            calculatedArea = room.materials.reduce((sum, material) => {
              const materialArea = parseFloat(material.area || 0);
              return sum + (isNaN(materialArea) || materialArea <= 0 ? 1 : materialArea);
            }, 0);
          }
          
          // Ensure we have a minimum of 1 m²
          calculatedArea = Math.max(calculatedArea, 1);
          
          // Return updated room with area
          return {
            ...room,
            area: calculatedArea
          };
        }
        return room;
      });
      
      // Ensure we have fixtures data
      if (!orderData.breakdown.fixtures) {
        orderData.breakdown.fixtures = {};
      }
      
      console.log("Processed order details:", orderData);
      setOrderDetails(orderData);
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch order details",
        icon: "error",
        timer: 3000,
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // View calculation details
  const handleViewCalculationDetails = (calculation) => {
    setSelectedOrder(calculation);
    setShowDetailsModal(true);
    
    // Use the calculation ID to fetch details
    fetchCalculationDetails(calculation._id);
  };

  // Fetch calculation details
  const fetchCalculationDetails = async (calculationId) => {
    setLoadingDetails(true);
    try {
      const api = axiosWithAuth();
      
      // Use the correct endpoint with getApiEndpoint utility
      const endpoint = getApiEndpoint(`repairs/calculations/admin/${calculationId}`);
      console.log("Fetching calculation details from:", endpoint);
      
      // Make the API call to get detailed calculation data
      const response = await api.get(endpoint);
      console.log("Calculation details API response:", response.data);
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Failed to fetch calculation details');
      }
      
      const calculationData = response.data.data;
      
      // Format the calculation data to match our component structure
      const formattedData = {
        _id: calculationData._id,
        tempId: calculationData.tempId,
        repairStyle: calculationData.repairStyle || null,
        repairTariff: calculationData.repairTariff || null,
        breakdown: calculationData.breakdown || {
          rooms: [],
          fixtures: {
            doors: { count: 0, materialCost: 0, serviceCost: 0 },
            windows: { count: 0, materialCost: 0, serviceCost: 0 }
          }
        },
        costs: calculationData.costs || {
          materialsCost: 0,
          serviceCost: 0,
          generalCost: 0,
          currency: 'AZN'
        },
        createdAt: calculationData.createdAt,
        updatedAt: calculationData.updatedAt,
        user: calculationData.user,
        status: calculationData.status,
        isRepairCalculation: true
      };
      
      console.log("Processed calculation details:", formattedData);
      setOrderDetails(formattedData);
    } catch (error) {
      console.error("Failed to fetch calculation details:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch calculation details",
        icon: "error",
        timer: 3000,
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Determine which data to export based on active tab
      const dataToExport = activeTab === "orders" ? orders : calculations;
      
      if (!dataToExport || dataToExport.length === 0) {
        Swal.fire({
          title: "No Data",
          text: "There is no data to export",
          icon: "warning",
          timer: 3000,
        });
        return;
      }
      
      // Prepare data for export
      const exportData = dataToExport.map(item => {
        if (activeTab === "orders") {
          // Export orders data with detailed breakdown
          const roomsData = item.rooms || [];
          const materialsData = roomsData.flatMap(room => 
            (room.materials || []).map(material => ({
              roomType: room.roomType,
              roomArea: room.area || 0,
              materialId: material.materialId || material.id || 'N/A',
              materialName: material.materialName || (typeof material.name === 'object' ? material.name?.text || material.name?.az : material.name) || 'N/A',
              brand: material.brand || 'N/A',
              category: material.category || 'N/A',
              unitOfMeasure: material.unitOfMeasure || 'unit',
              price: material.price || 0,
              servicePrice: material.servicePrice || 0,
              quantity: material.count || material.quantity || 1,
              area: material.area || room.area || 0,
              totalMaterialCost: (material.price || 0) * (material.count || material.quantity || 1) * (material.area || room.area || 1),
              totalServiceCost: (material.servicePrice || 0) * (material.count || material.quantity || 1) * (material.area || room.area || 1),
              store: material.store?.name || material.storeName || 'N/A',
              storeAddress: material.store?.address || 'N/A',
              storePhone: material.store?.phone || 'N/A',
              currency: material.currency || 'AZN',
              dependentMaterials: (material.dependentMaterials || []).map(dep => ({
                name: typeof dep.name === 'object' ? dep.name?.text || dep.name?.az : dep.name,
                price: dep.price || 0,
                servicePrice: dep.servicePrice || 0,
                quantity: dep.count || dep.quantity || 1,
                totalCost: (dep.price || 0 + dep.servicePrice || 0) * (dep.count || dep.quantity || 1) * (material.area || room.area || 1)
              }))
            }))
          );
          
          return {
            ID: item._id || item.id,
            Date: formatDate(item.createdAt),
            UserName: item.user?.name || "Anonymous",
            UserPhone: item.user?.phone || "N/A",
            UserEmail: item.user?.email || "N/A",
            Style: item.repairStyle?.name?.az || item.repairStyle?.name?.text || "N/A",
            Tariff: item.repairTariff?.name?.az || item.repairTariff?.name?.text || "N/A",
            RoomCount: roomsData.length,
            TotalRooms: roomsData.map(r => r.roomType).join(', '),
            MaterialsCost: formatNumber(item.materialsCost || item.costs?.materialsCost || 0),
            ServiceFee: formatNumber(item.serviceFee || item.costs?.serviceCost || 0),
            TotalCost: formatNumber(item.generalCost || item.costs?.generalCost || 0),
            Currency: item.currency || item.costs?.currency || "AZN",
            DoorsCount: item.fixtures?.doors?.count || 0,
            WindowsCount: item.fixtures?.windows?.count || 0,
            DoorsCost: formatNumber((item.fixtures?.doors?.materialCost || 0) + (item.fixtures?.doors?.serviceCost || 0)),
            WindowsCost: formatNumber((item.fixtures?.windows?.materialCost || 0) + (item.fixtures?.windows?.serviceCost || 0)),
            TotalMaterials: materialsData.length,
            MaterialsDetails: materialsData.map(m => 
              `Room: ${m.roomType} (${m.roomArea}m²) | Material: ${m.materialName} (ID: ${m.materialId}) | Brand: ${m.brand} | Category: ${m.category} | Qty: ${m.quantity} ${m.unitOfMeasure} | Area: ${m.area}m² | Unit Price: ${m.price} ${m.currency} | Service: ${m.servicePrice} ${m.currency} | Total Mat: ${m.totalMaterialCost.toFixed(2)} | Total Serv: ${m.totalServiceCost.toFixed(2)} | Store: ${m.store} (${m.storeAddress}, ${m.storePhone})${m.dependentMaterials.length > 0 ? ' | Deps: ' + m.dependentMaterials.map(d => `${d.name} (${d.price}+${d.servicePrice}=${d.totalCost.toFixed(2)})`).join(', ') : ''}`
            ).join(' || '),
            MaterialsBreakdown: materialsData.map(m => 
              `${m.roomType}: ${m.materialName} - ${m.quantity}x${m.area}m² = ${(m.totalMaterialCost + m.totalServiceCost).toFixed(2)} ${m.currency}`
            ).join(' | '),
            StoresUsed: [...new Set(materialsData.map(m => m.store))].filter(s => s !== 'N/A').join(', '),
            BrandsUsed: [...new Set(materialsData.map(m => m.brand))].filter(b => b !== 'N/A').join(', '),
            CategoriesUsed: [...new Set(materialsData.map(m => m.category))].filter(c => c !== 'N/A').join(', '),
            Status: item.status === true ? "Active" : "Inactive",
          };
        } else {
          // Export calculations data with detailed breakdown
          const roomsData = item.rooms || [];
          const materialsData = roomsData.flatMap(room => 
            (room.materials || []).map(material => ({
              roomType: room.roomType,
              roomArea: room.area || 0,
              materialId: material.materialId || material.id || 'N/A',
              materialName: material.materialName || (typeof material.name === 'object' ? material.name?.text || material.name?.az : material.name) || 'N/A',
              brand: material.brand || 'N/A',
              category: material.category || 'N/A',
              unitOfMeasure: material.unitOfMeasure || 'unit',
              price: material.price || 0,
              servicePrice: material.servicePrice || 0,
              quantity: material.count || material.quantity || 1,
              area: material.area || room.area || 0,
              totalMaterialCost: (material.price || 0) * (material.count || material.quantity || 1) * (material.area || room.area || 1),
              totalServiceCost: (material.servicePrice || 0) * (material.count || material.quantity || 1) * (material.area || room.area || 1),
              store: material.store?.name || material.storeName || 'N/A',
              storeAddress: material.store?.address || 'N/A',
              storePhone: material.store?.phone || 'N/A',
              currency: material.currency || 'AZN',
              dependentMaterials: (material.dependentMaterials || []).map(dep => ({
                name: typeof dep.name === 'object' ? dep.name?.text || dep.name?.az : dep.name,
                price: dep.price || 0,
                servicePrice: dep.servicePrice || 0,
                quantity: dep.count || dep.quantity || 1,
                totalCost: (dep.price || 0 + dep.servicePrice || 0) * (dep.count || dep.quantity || 1) * (material.area || room.area || 1)
              }))
            }))
          );
          
          return {
            ID: item._id || item.id,
            TempID: item.tempId || "N/A",
            Date: formatDate(item.createdAt),
            UserID: item.user?._id || "N/A",
            UserName: item.user?.name || "Anonymous",
            UserEmail: item.user?.email || "N/A",
            UserPhone: item.user?.phone || "N/A",
            RepairStyle: item.repairStyle?.name?.az || item.repairStyle?.name?.text || "N/A",
            RepairTariff: item.repairTariff?.name?.az || item.repairTariff?.name?.text || "N/A",
            RoomCount: item.roomCount || roomsData.length || 0,
            TotalRooms: roomsData.map(r => r.roomType).join(', '),
            RoomAreas: roomsData.map(r => `${r.roomType}: ${r.area || 0}m²`).join(', '),
            MaterialsCost: formatNumber(item.costs?.materialsCost || 0),
            ServiceCost: formatNumber(item.costs?.serviceCost || 0),
            TotalCost: formatNumber(item.costs?.generalCost || 0),
            Currency: item.costs?.currency || "AZN",
            DoorsCount: item.fixtures?.doors?.count || 0,
            WindowsCount: item.fixtures?.windows?.count || 0,
            DoorsMaterialCost: formatNumber(item.fixtures?.doors?.materialCost || 0),
            DoorsServiceCost: formatNumber(item.fixtures?.doors?.serviceCost || 0),
            WindowsMaterialCost: formatNumber(item.fixtures?.windows?.materialCost || 0),
            WindowsServiceCost: formatNumber(item.fixtures?.windows?.serviceCost || 0),
            RoomMaterialsCost: formatNumber(item.costs?.roomMaterialsCost || 0),
            RoomServiceCost: formatNumber(item.costs?.roomServiceCost || 0),
            DependentMaterialsCost: formatNumber(item.costs?.dependentMaterialsCost || 0),
            DependentServiceCost: formatNumber(item.costs?.dependentServiceCost || 0),
            Duration: item.costs?.duration || 1,
            TotalMaterials: materialsData.length,
            MaterialsDetails: materialsData.map(m => 
              `Room: ${m.roomType} (${m.roomArea}m²) | Material: ${m.materialName} (ID: ${m.materialId}) | Brand: ${m.brand} | Category: ${m.category} | Qty: ${m.quantity} ${m.unitOfMeasure} | Area: ${m.area}m² | Unit Price: ${m.price} ${m.currency} | Service: ${m.servicePrice} ${m.currency} | Total Mat: ${m.totalMaterialCost.toFixed(2)} | Total Serv: ${m.totalServiceCost.toFixed(2)} | Store: ${m.store} (${m.storeAddress}, ${m.storePhone})${m.dependentMaterials.length > 0 ? ' | Deps: ' + m.dependentMaterials.map(d => `${d.name} (${d.price}+${d.servicePrice}=${d.totalCost.toFixed(2)})`).join(', ') : ''}`
            ).join(' || '),
            RoomBreakdown: roomsData.map(r => 
              `${r.roomType} (${r.area}m²): Mat:${formatNumber(r.materialCost || 0)} Serv:${formatNumber(r.serviceCost || 0)} Total:${formatNumber(r.totalCost || 0)} ${item.costs?.currency || 'AZN'}`
            ).join(' | '),
            MaterialsBreakdown: materialsData.map(m => 
              `${m.roomType}: ${m.materialName} - ${m.quantity}x${m.area}m² = ${(m.totalMaterialCost + m.totalServiceCost).toFixed(2)} ${m.currency}`
            ).join(' | '),
            StoresUsed: [...new Set(materialsData.map(m => m.store))].filter(s => s !== 'N/A').join(', '),
            BrandsUsed: [...new Set(materialsData.map(m => m.brand))].filter(b => b !== 'N/A').join(', '),
            CategoriesUsed: [...new Set(materialsData.map(m => m.category))].filter(c => c !== 'N/A').join(', '),
            Status: item.status || "draft",
          };
        }
      });
      
      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 25 }, // ID
        { wch: 20 }, // Date/TempID
        { wch: 20 }, // User info
        { wch: 15 }, // Phone/Email
        { wch: 15 }, // Style/Tariff
        { wch: 10 }, // Counts
        { wch: 30 }, // Rooms
        { wch: 15 }, // Costs
        { wch: 50 }, // Materials Details
        { wch: 30 }, // Room Breakdown
      ];
      worksheet['!cols'] = columnWidths;
      
      // Set the title based on active tab
      const title = activeTab === "orders" ? "Orders_Detailed" : "Calculations_Detailed";
      XLSX.utils.book_append_sheet(workbook, worksheet, title);
      
      // Create detailed materials sheet
      const materialsData = [];
      dataToExport.forEach((item, itemIndex) => {
        const roomsData = activeTab === "orders" ? (item.rooms || []) : (item.rooms || []);
        
        roomsData.forEach((room, roomIndex) => {
          if (room.materials && room.materials.length > 0) {
            room.materials.forEach((material, matIndex) => {
              const materialName = material.materialName || 
                                 (typeof material.name === 'object' ? material.name?.text || material.name?.az : material.name) || 
                                 'N/A';
              
              const quantity = material.count || material.quantity || 1;
              const area = material.area || room.area || 1;
              const unitPrice = material.price || 0;
              const servicePrice = material.servicePrice || 0;
              const totalMaterialCost = unitPrice * quantity * area;
              const totalServiceCost = servicePrice * quantity * area;
              const totalCost = totalMaterialCost + totalServiceCost;
              
              materialsData.push({
                OrderID: item._id || item.id,
                TempID: item.tempId || "N/A",
                OrderDate: formatDate(item.createdAt),
                CustomerName: item.user?.name || "Anonymous",
                CustomerEmail: item.user?.email || "N/A",
                CustomerPhone: item.user?.phone || "N/A",
                RepairStyle: item.repairStyle?.name?.az || item.repairStyle?.name?.text || "N/A",
                RepairTariff: item.repairTariff?.name?.az || item.repairTariff?.name?.text || "N/A",
                RoomNumber: roomIndex + 1,
                RoomType: room.roomType || `Room ${roomIndex + 1}`,
                RoomArea: `${formatNumber(room.area || 0)} m²`,
                MaterialNumber: matIndex + 1,
                MaterialID: material.materialId || material.id || "N/A",
                MaterialName: materialName,
                MaterialBrand: material.brand || "N/A",
                MaterialCategory: material.category || "N/A",
                UnitOfMeasure: material.unitOfMeasure || "unit",
                Quantity: `${formatNumber(quantity)} ${material.unitOfMeasure || "unit"}`,
                Area: `${formatNumber(area)} m²`,
                UnitPrice: `${formatNumber(unitPrice)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`,
                ServiceFee: `${formatNumber(servicePrice)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`,
                TotalMaterialCost: `${formatNumber(totalMaterialCost)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`,
                TotalServiceCost: `${formatNumber(totalServiceCost)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`,
                TotalCost: `${formatNumber(totalCost)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`,
                StoreName: material.storeInfo?.name || material.store?.name || "No Store",
                StoreAddress: material.storeInfo?.address || material.store?.address || "N/A",
                StorePhone: material.storeInfo?.phone || material.store?.phone || "N/A",
                Currency: material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"
              });
              
              // Add dependent materials
              if (material.dependentMaterials && material.dependentMaterials.length > 0) {
                material.dependentMaterials.forEach((depMat, depIndex) => {
                  const depName = depMat.name?.text || depMat.name?.az || `Dependent Material ${depIndex + 1}`;
                  const depQuantity = depMat.count || depMat.quantity || 1;
                  const depUnitPrice = depMat.price || 0;
                  const depServicePrice = depMat.servicePrice || 0;
                  const depTotalMaterialCost = depUnitPrice * depQuantity * area;
                  const depTotalServiceCost = depServicePrice * depQuantity * area;
                  const depTotalCost = depTotalMaterialCost + depTotalServiceCost;
                  
                  materialsData.push({
                    OrderID: item._id || item.id,
                    TempID: item.tempId || "N/A",
                    OrderDate: formatDate(item.createdAt),
                    CustomerName: item.user?.name || "Anonymous",
                    CustomerEmail: item.user?.email || "N/A",
                    CustomerPhone: item.user?.phone || "N/A",
                    RepairStyle: item.repairStyle?.name?.az || item.repairStyle?.name?.text || "N/A",
                    RepairTariff: item.repairTariff?.name?.az || item.repairTariff?.name?.text || "N/A",
                    RoomNumber: roomIndex + 1,
                    RoomType: room.roomType || `Room ${roomIndex + 1}`,
                    RoomArea: `${formatNumber(room.area || 0)} m²`,
                    MaterialNumber: `${matIndex + 1}.${depIndex + 1}`,
                    MaterialID: depMat.materialId || depMat.id || "N/A",
                    MaterialName: `↳ ${depName} (Dependent)`,
                    MaterialBrand: depMat.brand || "N/A",
                    MaterialCategory: depMat.category || "Dependent",
                    UnitOfMeasure: depMat.unitOfMeasure || "unit",
                    Quantity: `${formatNumber(depQuantity)} ${depMat.unitOfMeasure || "unit"}`,
                    Area: `${formatNumber(area)} m²`,
                    UnitPrice: `${formatNumber(depUnitPrice)} ${depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"}`,
                    ServiceFee: `${formatNumber(depServicePrice)} ${depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"}`,
                    TotalMaterialCost: `${formatNumber(depTotalMaterialCost)} ${depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"}`,
                    TotalServiceCost: `${formatNumber(depTotalServiceCost)} ${depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"}`,
                    TotalCost: `${formatNumber(depTotalCost)} ${depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"}`,
                    StoreName: depMat.storeInfo?.name || material.storeInfo?.name || material.store?.name || "No Store",
                    StoreAddress: depMat.storeInfo?.address || material.storeInfo?.address || material.store?.address || "N/A",
                    StorePhone: depMat.storeInfo?.phone || material.storeInfo?.phone || material.store?.phone || "N/A",
                    Currency: depMat.currency || material.currency || orderDetails.costs?.currency || "AZN"
                  });
                });
              }
            });
          }
        });
      });
      
      // Create materials worksheet if we have materials data
      if (materialsData.length > 0) {
        const materialsWorksheet = XLSX.utils.json_to_sheet(materialsData);
        
        // Set column widths for materials sheet
        const materialsColumnWidths = [
          { wch: 20 }, // OrderID
          { wch: 15 }, // TempID
          { wch: 15 }, // OrderDate
          { wch: 20 }, // CustomerName
          { wch: 25 }, // CustomerEmail
          { wch: 15 }, // CustomerPhone
          { wch: 15 }, // RepairStyle
          { wch: 15 }, // RepairTariff
          { wch: 8 },  // RoomNumber
          { wch: 15 }, // RoomType
          { wch: 10 }, // RoomArea
          { wch: 8 },  // MaterialNumber
          { wch: 15 }, // MaterialID
          { wch: 30 }, // MaterialName
          { wch: 15 }, // MaterialBrand
          { wch: 15 }, // MaterialCategory
          { wch: 12 }, // UnitOfMeasure
          { wch: 12 }, // Quantity
          { wch: 10 }, // Area
          { wch: 12 }, // UnitPrice
          { wch: 12 }, // ServiceFee
          { wch: 15 }, // TotalMaterialCost
          { wch: 15 }, // TotalServiceCost
          { wch: 15 }, // TotalCost
          { wch: 20 }, // StoreName
          { wch: 25 }, // StoreAddress
          { wch: 15 }, // StorePhone
          { wch: 8 }   // Currency
        ];
        materialsWorksheet['!cols'] = materialsColumnWidths;
        
        XLSX.utils.book_append_sheet(workbook, materialsWorksheet, "Materials_Details");
      }
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Get current date for filename
      const date = new Date().toISOString().split('T')[0];
      saveAs(data, `eProrab_${title}_${date}.xlsx`);
      
      Swal.fire({
        title: "Success",
        text: `${title} data exported successfully with detailed breakdown`,
        icon: "success",
        timer: 3000,
      });
    } catch (error) {
      console.error("Failed to export data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to export data",
        icon: "error",
        timer: 3000,
      });
    }
  };

  // Export single order/calculation details to Excel
  const exportSingleToExcel = () => {
    try {
      if (!orderDetails) {
        Swal.fire({
          title: "No Data",
          text: "No order details available to export",
          icon: "warning",
          timer: 3000,
        });
        return;
      }

      // Prepare detailed export data
      const exportData = [];
      
      // Basic information
      exportData.push({
        Section: "BASIC_INFO",
        Field: "Order ID",
        Value: orderDetails._id || "N/A"
      });
      
      exportData.push({
        Section: "BASIC_INFO",
        Field: "Temp ID",
        Value: orderDetails.tempId || "N/A"
      });
      
      exportData.push({
        Section: "BASIC_INFO",
        Field: "Date Created",
        Value: formatDate(orderDetails.createdAt)
      });
      
      exportData.push({
        Section: "BASIC_INFO",
        Field: "Status",
        Value: orderDetails.status || "N/A"
      });

      // User information
      if (orderDetails.user) {
        exportData.push({
          Section: "USER_INFO",
          Field: "User Name",
          Value: orderDetails.user.name || "N/A"
        });
        
        exportData.push({
          Section: "USER_INFO",
          Field: "User Email",
          Value: orderDetails.user.email || "N/A"
        });
        
        exportData.push({
          Section: "USER_INFO",
          Field: "User Phone",
          Value: orderDetails.user.phone || "N/A"
        });
      }

      // Repair information
      exportData.push({
        Section: "REPAIR_INFO",
        Field: "Repair Style",
        Value: orderDetails.repairStyle?.name?.az || orderDetails.repairStyle?.name?.text || "N/A"
      });
      
      exportData.push({
        Section: "REPAIR_INFO",
        Field: "Repair Tariff",
        Value: orderDetails.repairTariff?.name?.az || orderDetails.repairTariff?.name?.text || "N/A"
      });

      // Cost summary
      const costs = orderDetails.costs || {};
      exportData.push({
        Section: "COST_SUMMARY",
        Field: "Materials Cost",
        Value: `${formatNumber(costs.materialsCost || orderDetails.materialsCost || 0)} ${costs.currency || orderDetails.currency || "AZN"}`
      });
      
      exportData.push({
        Section: "COST_SUMMARY",
        Field: "Service Cost",
        Value: `${formatNumber(costs.serviceCost || orderDetails.serviceFee || 0)} ${costs.currency || orderDetails.currency || "AZN"}`
      });
      
      exportData.push({
        Section: "COST_SUMMARY",
        Field: "Total Cost",
        Value: `${formatNumber(costs.generalCost || orderDetails.generalCost || 0)} ${costs.currency || orderDetails.currency || "AZN"}`
      });

      // Detailed cost breakdown
      if (costs.roomMaterialsCost || costs.doorMaterialsCost || costs.dependentMaterialsCost) {
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Room Materials Cost",
          Value: `${formatNumber(costs.roomMaterialsCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Room Service Cost",
          Value: `${formatNumber(costs.roomServiceCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Door Materials Cost",
          Value: `${formatNumber(costs.doorMaterialsCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Door Service Cost",
          Value: `${formatNumber(costs.doorServiceCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Window Materials Cost",
          Value: `${formatNumber(costs.windowMaterialsCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Window Service Cost",
          Value: `${formatNumber(costs.windowServiceCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Dependent Materials Cost",
          Value: `${formatNumber(costs.dependentMaterialsCost || 0)} ${costs.currency || "AZN"}`
        });
        
        exportData.push({
          Section: "DETAILED_COSTS",
          Field: "Dependent Service Cost",
          Value: `${formatNumber(costs.dependentServiceCost || 0)} ${costs.currency || "AZN"}`
        });
      }

      // Fixtures information
      if (orderDetails.breakdown?.fixtures) {
        const fixtures = orderDetails.breakdown.fixtures;
        
        if (fixtures.doors && fixtures.doors.count > 0) {
          exportData.push({
            Section: "FIXTURES",
            Field: "Doors Count",
            Value: fixtures.doors.count
          });
          
          exportData.push({
            Section: "FIXTURES",
            Field: "Doors Material Cost",
            Value: `${formatNumber(fixtures.doors.materialCost || 0)} ${costs.currency || "AZN"}`
          });
          
          exportData.push({
            Section: "FIXTURES",
            Field: "Doors Service Cost",
            Value: `${formatNumber(fixtures.doors.serviceCost || 0)} ${costs.currency || "AZN"}`
          });
        }
        
        if (fixtures.windows && fixtures.windows.count > 0) {
          exportData.push({
            Section: "FIXTURES",
            Field: "Windows Count",
            Value: fixtures.windows.count
          });
          
          exportData.push({
            Section: "FIXTURES",
            Field: "Windows Material Cost",
            Value: `${formatNumber(fixtures.windows.materialCost || 0)} ${costs.currency || "AZN"}`
          });
          
          exportData.push({
            Section: "FIXTURES",
            Field: "Windows Service Cost",
            Value: `${formatNumber(fixtures.windows.serviceCost || 0)} ${costs.currency || "AZN"}`
          });
        }
      }

      // Room details
      if (orderDetails.breakdown?.rooms && orderDetails.breakdown.rooms.length > 0) {
        orderDetails.breakdown.rooms.forEach((room, roomIndex) => {
          exportData.push({
            Section: `ROOM_${roomIndex + 1}`,
            Field: "Room Type",
            Value: room.roomType || `Room ${roomIndex + 1}`
          });
          
          exportData.push({
            Section: `ROOM_${roomIndex + 1}`,
            Field: "Room Area",
            Value: `${formatNumber(room.area || 0)} m²`
          });
          
          exportData.push({
            Section: `ROOM_${roomIndex + 1}`,
            Field: "Room Material Cost",
            Value: `${formatNumber(room.materialCost || 0)} ${costs.currency || "AZN"}`
          });
          
          exportData.push({
            Section: `ROOM_${roomIndex + 1}`,
            Field: "Room Service Cost",
            Value: `${formatNumber(room.serviceCost || 0)} ${costs.currency || "AZN"}`
          });
          
          exportData.push({
            Section: `ROOM_${roomIndex + 1}`,
            Field: "Room Total Cost",
            Value: `${formatNumber(room.totalCost || 0)} ${costs.currency || "AZN"}`
          });

          // Materials in this room
          if (room.materials && room.materials.length > 0) {
            room.materials.forEach((material, matIndex) => {
              const materialName = material.name?.text || material.name?.az || material.materialName || `Material ${matIndex + 1}`;
              
              exportData.push({
                Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                Field: `Material ${matIndex + 1} Name`,
                Value: materialName
              });
              
              exportData.push({
                Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                Field: `Material ${matIndex + 1} Quantity`,
                Value: `${formatNumber(material.quantity || material.count || 1)} ${material.unitOfMeasure || "unit"}`
              });
              
              exportData.push({
                Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                Field: `Material ${matIndex + 1} Unit Price`,
                Value: `${formatNumber(material.price || 0)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`
              });
              
              exportData.push({
                Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                Field: `Material ${matIndex + 1} Service Price`,
                Value: `${formatNumber(material.servicePrice || 0)} ${material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}`
              });
              
              exportData.push({
                Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                Field: `Material ${matIndex + 1} Store`,
                Value: material.storeInfo?.name || material.store?.name || "N/A"
              });

              // Dependent materials
              if (material.dependentMaterials && material.dependentMaterials.length > 0) {
                material.dependentMaterials.forEach((depMat, depIndex) => {
                  const depName = depMat.name?.text || depMat.name?.az || `Dependent Material ${depIndex + 1}`;
                  
                  exportData.push({
                    Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                    Field: `Material ${matIndex + 1} Dependent ${depIndex + 1} Name`,
                    Value: depName
                  });
                  
                  exportData.push({
                    Section: `ROOM_${roomIndex + 1}_MATERIALS`,
                    Field: `Material ${matIndex + 1} Dependent ${depIndex + 1} Price`,
                    Value: `${formatNumber(depMat.price || 0)} + ${formatNumber(depMat.servicePrice || 0)} ${depMat.currency || costs.currency || "AZN"}`
                  });
                });
              }
            });
          }
        });
      }

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 25 }, // Section
        { wch: 30 }, // Field
        { wch: 40 }  // Value
      ];
      
      XLSX.utils.book_append_sheet(workbook, worksheet, "Order_Details");
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Get current date for filename
      const date = new Date().toISOString().split('T')[0];
      const orderId = orderDetails._id?.substring(0, 8) || 'unknown';
      saveAs(data, `eProrab_Order_${orderId}_${date}.xlsx`);
      
      Swal.fire({
        title: "Success",
        text: "Order details exported successfully",
        icon: "success",
        timer: 3000,
      });
    } catch (error) {
      console.error("Failed to export order details:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to export order details",
        icon: "error",
        timer: 3000,
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Format number with 2 decimal places
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    const rounded = Math.round(numValue * 100) / 100;
    return rounded.toFixed(2);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === true) {
      return <Badge bg="success">Active</Badge>;
    } else {
      return <Badge bg="danger">Inactive</Badge>;
    }
  };

  // Get customer name or anonymous
  const getCustomerName = (order) => {
    if (!order.user) {
      return <span className="text-muted">Anonymous</span>;
    }
    return order.user.name || order.user.phone || `User #${order.user._id}`;
  };

  // Get room count for order
  const getRoomCount = (order) => {
    // Check multiple possible locations for room data
    const rooms = 
      order.rooms || 
      order.breakdown?.rooms || 
      order.breakdownResponse?.breakdown?.rooms || [];
    
    return rooms.length || 0;
  };

  // Get store badge
  const getStoreBadge = (store) => {
    if (!store || !store.name) {
      return <Badge bg="secondary">No Store</Badge>;
    }
    
    return <Badge bg="info">{store.name}</Badge>;
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle date filter change
  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchOrders();
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle calculations page change
  const handleCalculationsPageChange = (newPage) => {
    setCalculationsPage(newPage);
  };

  // Render pagination controls
  const renderPagination = () => {
    const pages = [];
    const maxButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    
    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </Button>
      </li>
    );
    
    // Page buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <Button 
            className="page-link" 
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>
        </li>
      );
    }
    
    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </Button>
      </li>
    );
    
    return (
      <nav>
        <ul className="pagination mb-0">
          {pages}
        </ul>
      </nav>
    );
  };

  // Render calculations pagination
  const renderCalculationsPagination = () => {
    if (calculationsTotalPages <= 1) return null;
    
    const items = [];
    const maxPagesToShow = 5;
    const startPage = Math.max(1, calculationsPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(calculationsTotalPages, startPage + maxPagesToShow - 1);
    
    // Previous button
    items.push(
      <li key="prev" className={`page-item ${calculationsPage === 1 ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handleCalculationsPageChange(calculationsPage - 1)}
          disabled={calculationsPage === 1}
        >
          &laquo;
        </Button>
      </li>
    );
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <li key={i} className={`page-item ${calculationsPage === i ? 'active' : ''}`}>
          <Button 
            className="page-link" 
            onClick={() => handleCalculationsPageChange(i)}
          >
            {i}
          </Button>
        </li>
      );
    }
    
    // Next button
    items.push(
      <li key="next" className={`page-item ${calculationsPage === calculationsTotalPages ? 'disabled' : ''}`}>
        <Button 
          className="page-link" 
          onClick={() => handleCalculationsPageChange(calculationsPage + 1)}
          disabled={calculationsPage === calculationsTotalPages}
        >
          &raquo;
        </Button>
      </li>
    );
    
    return (
      <nav aria-label="Calculations pagination">
        <ul className="pagination mb-0">
          {items}
        </ul>
      </nav>
    );
  };

  return (
    <div className="order-manager">
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Order Manager</h4>
            <div>
              <Button
                variant="outline-success"
                className="me-2"
                onClick={exportToExcel}
                disabled={(activeTab === "orders" && orders.length === 0) || 
                          (activeTab === "calculations" && calculations.length === 0) ||
                          loading || loadingCalculations}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Export
              </Button>
              <Button
                variant="outline-primary"
                onClick={activeTab === "orders" ? fetchOrders : fetchCalculations}
                disabled={activeTab === "orders" ? loading : loadingCalculations}
              >
                {(activeTab === "orders" && loading) || (activeTab === "calculations" && loadingCalculations) ? 
                  <Spinner size="sm" animation="border" /> : 
                  <i className="bi bi-arrow-clockwise"></i>}
              </Button>
            </div>
          </div>
        </Card.Header>
        
        <Card.Body>
          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="mb-4"
          >
            <Tab eventKey="orders" title="Orders">
              {/* Orders Content */}
              
              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select 
                      value={statusFilter} 
                      onChange={handleStatusFilterChange}
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={dateFilter.startDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={dateFilter.endDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <div className="d-flex">
                      <Form.Control
                        type="text"
                        placeholder="Search by ID, name, phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button 
                        variant="primary" 
                        className="ms-2"
                        onClick={handleSearch}
                      >
                        <i className="bi bi-search"></i>
                      </Button>
                    </div>
                  </Form.Group>
                </div>
              </div>
              
              {/* Orders Table */}
              {loading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  No orders found matching your criteria.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Style</th>
                        <th>Tariff</th>
                        <th className="text-center">Rooms</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => (
                        <tr key={order._id || order.id}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>{getCustomerName(order)}</td>
                          <td>{order.repairStyle?.name?.az || order.repairStyle?.name?.text || "N/A"}</td>
                          <td>{order.repairTariff?.name?.az || order.repairTariff?.name?.text || "N/A"}</td>
                          <td className="text-center">
                            <Badge bg="info">{getRoomCount(order)}</Badge>
                          </td>
                          <td className="text-end">
                            {formatNumber(order.materialsCost || order.costs?.materialsCost || 0)} {order.currency || "AZN"}
                          </td>
                          <td className="text-center">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleViewDetails(order)}
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing {Math.min((currentPage - 1) * pageSize + 1, orders.length)} to {Math.min(currentPage * pageSize, orders.length)} of {orders.length} entries
                    </div>
                    {renderPagination()}
                  </div>
                </div>
              )}
            </Tab>
            
            <Tab eventKey="calculations" title="Calculations">
              {/* Calculations Content */}
              
              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select 
                      value={statusFilter} 
                      onChange={handleStatusFilterChange}
                    >
                      <option value="all">All</option>
                      <option value="draft">Draft</option>
                      <option value="completed">Completed</option>
                      <option value="deleted">Deleted</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={dateFilter.startDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={dateFilter.endDate}
                      onChange={handleDateFilterChange}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <div className="d-flex">
                      <Form.Control
                        type="text"
                        placeholder="Search by ID or temp ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button 
                        variant="primary" 
                        className="ms-2"
                        onClick={handleSearch}
                      >
                        <i className="bi bi-search"></i>
                      </Button>
                    </div>
                  </Form.Group>
                </div>
              </div>
              
              {/* Calculations Table */}
              {loadingCalculations ? (
                <div className="text-center my-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading calculations...</p>
                </div>
              ) : calculations.length === 0 ? (
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  No calculations found matching your criteria.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Temp ID</th>
                        <th>User</th>
                        <th className="text-center">Rooms</th>
                        <th>Repair Style</th>
                        <th>Repair Tariff</th>
                        <th className="text-end">Materials</th>
                        <th className="text-end">Service</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Doors/Windows</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculations.map((calc, index) => (
                        <tr key={calc._id}>
                          <td>{(calculationsPage - 1) * pageSize + index + 1}</td>
                          <td>{formatDate(calc.createdAt)}</td>
                          <td>
                            <small className="text-muted">{calc.tempId || "N/A"}</small>
                          </td>
                          <td>{calc.user?.name || "Anonymous"}</td>
                          <td className="text-center">
                            <Badge bg="info">{calc.roomCount || 0}</Badge>
                          </td>
                          <td>
                            {calc.repairStyle?.name?.az || calc.repairStyle?.name?.text || "N/A"}
                          </td>
                          <td>
                            {calc.repairTariff?.name?.az || calc.repairTariff?.name?.text || "N/A"}
                          </td>
                          <td className="text-end">
                            {formatNumber(calc.costs?.materialsCost || 0)} {calc.costs?.currency || "AZN"}
                          </td>
                          <td className="text-end">
                            {formatNumber(calc.costs?.serviceCost || 0)} {calc.costs?.currency || "AZN"}
                          </td>
                          <td className="text-end">
                            {formatNumber(calc.costs?.generalCost || 0)} {calc.costs?.currency || "AZN"}
                          </td>
                          <td className="text-center">
                            <Badge bg={calc.status === "draft" ? "warning" : calc.status === "completed" ? "success" : "danger"}>
                              {calc.status}
                            </Badge>
                          </td>
                          <td className="text-center">
                            {calc.fixtures ? (
                              <div>
                                {calc.fixtures.doors && calc.fixtures.doors.count > 0 && (
                                  <Badge bg="secondary" className="me-1">
                                    <i className="bi bi-door-open me-1"></i>
                                    {calc.fixtures.doors.count}
                                  </Badge>
                                )}
                                {calc.fixtures.windows && calc.fixtures.windows.count > 0 && (
                                  <Badge bg="info">
                                    <i className="bi bi-window me-1"></i>
                                    {calc.fixtures.windows.count}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleViewCalculationDetails(calc)}
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing {Math.min((calculationsPage - 1) * pageSize + 1, calculations.length)} to {Math.min(calculationsPage * pageSize, calculations.length)} of {calculations.length} entries
                    </div>
                    {renderCalculationsPagination()}
                  </div>
                </div>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
      
      {/* Order Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setOrderDetails(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Order Details #{selectedOrder?.id || selectedOrder?._id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading order details...</p>
            </div>
          ) : !orderDetails ? (
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              No details available for this order.
            </div>
          ) : (
            <Tabs defaultActiveKey="summary" id="order-details-tabs" className="mb-3">
              {/* Summary Tab */}
              <Tab eventKey="summary" title="Summary">
                <Card className="mb-3">
                  <Card.Header>
                    <h5 className="mb-0">Order Summary</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h6 className="mb-3">Customer Information</h6>
                        <Table bordered size="sm">
                          <tbody>
                            <tr>
                              <th>Name</th>
                              <td>{orderDetails.user?.name || "Anonymous"}</td>
                            </tr>
                            <tr>
                              <th>Phone</th>
                              <td>{orderDetails.user?.phone || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Email</th>
                              <td>{orderDetails.user?.email || "N/A"}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                      <Col md={6}>
                        <h6 className="mb-3">Order Information</h6>
                        <Table bordered size="sm">
                          <tbody>
                            <tr>
                              <th>Date</th>
                              <td>{formatDate(orderDetails.createdAt)}</td>
                            </tr>
                            <tr>
                              <th>Status</th>
                              <td>{getStatusBadge(orderDetails.status)}</td>
                            </tr>
                            <tr>
                              <th>Repair Style</th>
                              <td>{orderDetails.repairStyle?.name?.az || orderDetails.repairStyle?.name?.text || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Repair Tariff</th>
                              <td>{orderDetails.repairTariff?.name?.az || orderDetails.repairTariff?.name?.text || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Fixtures</th>
                              <td>
                                {orderDetails.breakdown?.fixtures ? (
                                  <div>
                                    {orderDetails.breakdown.fixtures.doors?.count > 0 && (
                                      <Badge bg="secondary" className="me-1">
                                        <i className="bi bi-door-open me-1"></i>
                                        Doors: {orderDetails.breakdown.fixtures.doors.count}
                                      </Badge>
                                    )}
                                    {orderDetails.breakdown.fixtures.windows?.count > 0 && (
                                      <Badge bg="info" className="me-1">
                                        <i className="bi bi-window me-1"></i>
                                        Windows: {orderDetails.breakdown.fixtures.windows.count}
                                      </Badge>
                                    )}
                                    {(!orderDetails.breakdown.fixtures.doors?.count && !orderDetails.breakdown.fixtures.windows?.count) && 
                                      "No fixtures"
                                    }
                                  </div>
                                ) : "No fixtures"}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                    
                    <h6 className="mt-4 mb-3">Cost Summary</h6>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h6 className="card-title">Materials Cost</h6>
                            <p className="card-text fs-5">
                              {formatNumber(orderDetails.materialsCost || orderDetails.costs?.materialsCost || 0)} {orderDetails.currency || "AZN"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h6 className="card-title">Service Cost</h6>
                            <p className="card-text fs-5">
                              {formatNumber(orderDetails.serviceFee || orderDetails.costs?.serviceCost || 0)} {orderDetails.currency || "AZN"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-light">
                          <div className="card-body text-center">
                            <h6 className="card-title">Doors/Windows</h6>
                            <p className="card-text fs-5">
                              {formatNumber(
                                (orderDetails.costs?.doorMaterialsCost || 0) + 
                                (orderDetails.costs?.windowMaterialsCost || 0) +
                                (orderDetails.costs?.doorServiceCost || 0) + 
                                (orderDetails.costs?.windowServiceCost || 0)
                              )} {orderDetails.currency || "AZN"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-primary text-white">
                          <div className="card-body text-center">
                            <h6 className="card-title">Total Cost</h6>
                            <p className="card-text fs-4 fw-bold">
                              {formatNumber(orderDetails.generalCost || orderDetails.costs?.generalCost || 0)} {orderDetails.currency || "AZN"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
              
              {/* Rooms Tab */}
              <Tab eventKey="rooms" title="Rooms">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Room Details</h5>
                  </Card.Header>
                  <Card.Body>
                    {!orderDetails.breakdown?.rooms || orderDetails.breakdown.rooms.length === 0 ? (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        No room data available for this order.
                      </div>
                    ) : (
                      <div className="accordion" id="roomsAccordion">
                        {orderDetails.breakdown.rooms.map((room, index) => (
                          <div className="accordion-item" key={index}>
                            <h2 className="accordion-header">
                              <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#room-${index}`}
                              >
                                <div className="d-flex justify-content-between w-100 me-3">
                                  <span>
                                    {room.roomType || `Room ${index + 1}`} ({formatNumber(room.area || 0)} m²)
                                  </span>
                                  <span className="badge bg-primary">
                                    {formatNumber(room.totalCost || 0)} {orderDetails.currency || "AZN"}
                                  </span>
                                </div>
                              </button>
                            </h2>
                            <div
                              id={`room-${index}`}
                              className="accordion-collapse collapse"
                              data-bs-parent="#roomsAccordion"
                            >
                              <div className="accordion-body">
                                <h6 className="mb-3">Room Materials</h6>
                                {!room.materials || room.materials.length === 0 ? (
                                  <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    No materials data available for this room.
                                  </div>
                                ) : (
                                  <Table striped bordered hover size="sm">
                                    <thead>
                                      <tr>
                                        <th>Material</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Service Fee</th>
                                        <th>Store</th>
                                        <th>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.materials.map((material, matIndex) => (
                                        <React.Fragment key={matIndex}>
                                          <tr>
                                            <td>{material.name?.text || material.name?.az || material.materialName || ""}</td>
                                            <td>{formatNumber(material.quantity || material.count || 0)} {material.unitOfMeasure || "unit"}</td>
                                            <td>{formatNumber(material.price || 0)} {material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}</td>
                                            <td>{formatNumber(material.servicePrice || 0)} {material.currency || orderDetails.costs?.currency || orderDetails.currency || "AZN"}</td>
                                            <td>{getStoreBadge(material.storeInfo || material.store || orderDetails.store || orderDetails.storeInfo)}</td>
                                            <td>
                                              {formatNumber(
                                                (material.price || 0) * (material.quantity || material.count || 1) * (material.area || room.area || 1) + 
                                                (material.servicePrice || 0) * (material.quantity || material.count || 1) * (material.area || room.area || 1)
                                              )} {material.currency || "AZN"}
                                            </td>
                                          </tr>
                                          {material.dependentMaterials?.map((depMat, depIndex) => (
                                            <tr key={`dep-${depIndex}`} className="table-secondary">
                                              <td className="ps-4">
                                                <i className="bi bi-arrow-return-right me-2"></i>
                                                {depMat.name?.text || depMat.name?.az || ""}
                                              </td>
                                              <td>{formatNumber(depMat.quantity || depMat.count || 0)} {depMat.unitOfMeasure || "unit"}</td>
                                              <td>{formatNumber(depMat.price || 0)} {depMat.currency || material.currency || "AZN"}</td>
                                              <td>{formatNumber(depMat.servicePrice || 0)} {depMat.currency || material.currency || "AZN"}</td>
                                              <td>{getStoreBadge(depMat.storeInfo || material.storeInfo || orderDetails.store || orderDetails.storeInfo)}</td>
                                              <td>
                                                {formatNumber(
                                                  (depMat.price || 0) * (depMat.quantity || depMat.count || 1) * (material.area || room.area || 1) + 
                                                  (depMat.servicePrice || 0) * (depMat.quantity || depMat.count || 1) * (material.area || room.area || 1)
                                                )} {depMat.currency || material.currency || "AZN"}
                                              </td>
                                            </tr>
                                          ))}
                                        </React.Fragment>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr>
                                        <th colSpan={5}>Room Total</th>
                                        <th>{formatNumber(room.totalCost || 0)} {orderDetails.currency || "AZN"}</th>
                                      </tr>
                                    </tfoot>
                                  </Table>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
              
              {/* Fixtures Tab */}
              <Tab eventKey="fixtures" title="Doors & Windows">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Doors & Windows</h5>
                  </Card.Header>
                  <Card.Body>
                    {!orderDetails.breakdown?.fixtures ? (
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        No fixtures data available for this order.
                      </div>
                    ) : (
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Count</th>
                            <th>Material Cost</th>
                            <th>Service Cost</th>
                            <th>Total Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderDetails.breakdown.fixtures.doors && 
                           orderDetails.breakdown.fixtures.doors.count > 0 && (
                            <tr>
                              <td>Doors</td>
                              <td>{orderDetails.breakdown.fixtures.doors.count}</td>
                              <td>{formatNumber(orderDetails.breakdown.fixtures.doors.materialCost || 0)} {orderDetails.currency || "AZN"}</td>
                              <td>{formatNumber(orderDetails.breakdown.fixtures.doors.serviceCost || 0)} {orderDetails.currency || "AZN"}</td>
                              <td>
                                {formatNumber(
                                  (orderDetails.breakdown.fixtures.doors.materialCost || 0) +
                                  (orderDetails.breakdown.fixtures.doors.serviceCost || 0)
                                )} {orderDetails.currency || "AZN"}
                              </td>
                            </tr>
                          )}
                          
                          {orderDetails.breakdown.fixtures.windows && 
                           orderDetails.breakdown.fixtures.windows.count > 0 && (
                            <tr>
                              <td>Windows</td>
                              <td>{orderDetails.breakdown.fixtures.windows.count}</td>
                              <td>{formatNumber(orderDetails.breakdown.fixtures.windows.materialCost || 0)} {orderDetails.currency || "AZN"}</td>
                              <td>{formatNumber(orderDetails.breakdown.fixtures.windows.serviceCost || 0)} {orderDetails.currency || "AZN"}</td>
                              <td>
                                {formatNumber(
                                  (orderDetails.breakdown.fixtures.windows.materialCost || 0) +
                                  (orderDetails.breakdown.fixtures.windows.serviceCost || 0)
                                )} {orderDetails.currency || "AZN"}
                              </td>
                            </tr>
                          )}
                          
                          {(!orderDetails.breakdown.fixtures.doors || orderDetails.breakdown.fixtures.doors.count <= 0) &&
                           (!orderDetails.breakdown.fixtures.windows || orderDetails.breakdown.fixtures.windows.count <= 0) && (
                            <tr>
                              <td colSpan={5} className="text-center">
                                No doors or windows in this order.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {(orderDetails.breakdown.fixtures.doors?.count > 0 || 
                          orderDetails.breakdown.fixtures.windows?.count > 0) && (
                          <tfoot>
                            <tr>
                              <th colSpan={4}>Total Fixtures Cost</th>
                              <th>
                                {formatNumber(
                                  (orderDetails.breakdown.fixtures.doors?.materialCost || 0) +
                                  (orderDetails.breakdown.fixtures.doors?.serviceCost || 0) +
                                  (orderDetails.breakdown.fixtures.windows?.materialCost || 0) +
                                  (orderDetails.breakdown.fixtures.windows?.serviceCost || 0)
                                )} {orderDetails.currency || "AZN"}
                              </th>
                            </tr>
                          </tfoot>
                        )}
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDetailsModal(false);
            setOrderDetails(null);
          }}>
            Close
          </Button>
          <Button variant="success" onClick={exportSingleToExcel}>
            <i className="bi bi-file-earmark-excel me-1"></i>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderManager; 